import { useMutation, useQueryClient } from '@tanstack/react-query'
import { BadgeCheck } from 'lucide-react'
import { useState } from 'react'

import { attestGroupHealth } from '@/api/groups-api'
import type { DualControlResult } from '@/api/types'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'

import { DualControlNotice, DualControlOutcome } from './dual-control'
import { GroupActionDialog } from './group-action-dialog'
import { GROUP_PERMISSIONS, useGroupPermissions } from './permissions'

/**
 * The cold-start judgement: a person answering for a group the formula cannot score.
 *
 * A brand-new group has no repayment history and no attendance window, so its health is honestly
 * "never computed" — and the round gate rightly refuses to lend on nothing. The way through was
 * designed from the start: an officer's countersigned attestation that the group is fit for a
 * first round, which expires rather than standing forever. The server has offered it all along
 * (`POST /groups/{id}/health/attestation`); this is the button the web never gave it. Found by
 * UAT asking "how do we get the initial score" — the eleventh-hour proof that a control without
 * a door on the screen is a control that does not exist.
 */
export function AttestHealthAction({ groupId }: { groupId: string }) {
  const permissions = useGroupPermissions()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [result, setResult] = useState<DualControlResult | null>(null)

  const attest = useMutation({
    mutationFn: () => attestGroupHealth(groupId, reason.trim()),
    onSuccess: (outcome) => {
      setResult(outcome)
      // Executed or queued, the health panel and the round gate re-read rather than assume.
      void queryClient.invalidateQueries({ queryKey: ['groups'] })
    },
  })

  if (!permissions.canAttestHealth) {
    return null
  }

  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        <BadgeCheck />
        Attest standing
      </Button>

      <GroupActionDialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          if (!next) {
            setReason('')
            setResult(null)
            attest.reset()
          }
        }}
        title="Attest this group's standing"
        description="A group with no history cannot be scored, and the round gate refuses to lend on nothing. Your attestation is the countersigned judgement that stands in until real repayments fill the window — it expires, and it is recorded under your name."
        notice={
          <DualControlNotice
            action="Attesting a group's standing"
            approvePermission={GROUP_PERMISSIONS.healthAttestApprove}
            canCountersign={permissions.canCountersignHealthAttest}
          />
        }
        outcome={
          result ? (
            <DualControlOutcome
              result={result}
              awaitingDescription="The attestation is stored and waiting for a countersignature. The group still reads as unscored until it is signed."
              appliedDescription="The group now reads as qualifying by attestation, and a first round can proceed while the judgement stands."
            />
          ) : null
        }
        confirmLabel="Attest"
        isPending={attest.isPending}
        disabled={reason.trim().length < 12}
        error={attest.isError ? attest.error : null}
        onConfirm={() => attest.mutate()}
      >
        <div className="space-y-1.5">
          <Label htmlFor="attest-reason">What you are attesting, and on what basis</Label>
          <textarea
            id="attest-reason"
            rows={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="e.g. Members known to the branch from prior cycles; meetings observed; leader's record."
            className="w-full rounded-control border border-border-subtle bg-surface px-3 py-2 text-sm text-heading transition-colors placeholder:text-muted hover:border-border-strong focus:border-brand-text focus:outline-none"
          />
          <p className="text-xs text-muted">
            This is what the countersigner reads before signing, and what an examiner reads when the
            round is later asked about.
          </p>
        </div>
      </GroupActionDialog>
    </>
  )
}
